import {
  inject,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import {
  FieldNotebookCrop,
  FieldNotebookEntry,
} from 'app/core/interface/field-notebook';
import { FieldNotebookService } from 'app/core/services/field-notebook.service';
import { languageCode } from './field-notebook.utils';
@Component({
  selector: 'app-notebook-crop-select',
  standalone: true,
  imports: [FormsModule, NgSelectModule, TranslateModule],
  template: `<ng-select
      [items]="options"
      bindLabel="label"
      bindValue="eppoCode"
      [ngModel]="value?.eppoCode"
      (ngModelChange)="choose($event)"
      [labelForId]="inputId"
      [searchable]="true"
      [searchFn]="search"
      [clearable]="true"
      [placeholder]="'field-notebook.crop-search' | translate"
      [notFoundText]="'field-notebook.no-data' | translate"
      [loadingText]="'field-notebook.loading' | translate"></ng-select>
    @if (failed) {
      <small role="alert">{{
        'field-notebook.catalog-error' | translate
      }}</small>
    }`,
})
export class NotebookCropSelectComponent implements OnInit, OnDestroy {
  @Input() value: FieldNotebookEntry['crop'];
  @Input() inputId = 'notebook-crop';
  @Output() valueChange = new EventEmitter<FieldNotebookEntry['crop']>();
  options: Array<{
    label: string;
    eppoCode: string;
    name: string;
    searchText: string;
  }> = [];
  failed = false;
  private catalog: FieldNotebookCrop[] = [];
  private readonly destroy$ = new Subject<void>();
  private readonly service = inject(FieldNotebookService);
  private readonly translate = inject(TranslateService);
  ngOnInit(): void {
    this.service
      .crops()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (catalog) => {
          this.catalog = catalog;
          this.localize();
        },
        error: () => {
          this.failed = true;
        },
      });
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.localize());
  }
  private localize(): void {
    const language = languageCode(this.translate.currentLang);
    this.options = this.catalog.map((crop) => ({
      name: crop.names[language],
      label: `${crop.names[language]} (${crop.eppoCode})`,
      searchText: this.normalize(
        [
          crop.eppoCode,
          ...Object.values(crop.names),
          ...(crop.aliases || []),
        ].join(' '),
      ),
      eppoCode: crop.eppoCode,
    }));
  }
  private normalize(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }
  readonly search = (term: string, item: { searchText: string }): boolean => {
    return item.searchText.includes(this.normalize(term));
  };
  choose(code?: string): void {
    const selected = this.options.find((crop) => crop.eppoCode === code);
    if (selected) {
      this.valueChange.emit({
        name: selected.name,
        eppoCode: selected.eppoCode,
        bbch: this.value?.bbch,
        nameOnDevice: this.value?.nameOnDevice,
      });
    } else {
      this.valueChange.emit({
        bbch: this.value?.bbch,
        nameOnDevice: this.value?.nameOnDevice,
      });
    }
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
